/* Mojito — collection-card color swatches as image thumbnails (Globo replacement).

   Reuses the theme's NATIVE collection swatches (style = "slider": a single
   horizontal scroll row of `.swatch__button`, each with the variant photo URL in
   `data-swatch-image`, an SEO `?variant=` `.swatch__link`, and the theme's built-in
   click->swap of the card's main image). This script adds:
     1) paints each swatch with its variant PHOTO (lazily, small CDN width),
     2) a subtle "N colors" count above each row,
     3) marks the ACTIVE color so the CSS can draw a light border on it ONLY,
     4) on click, CYCLES only the clicked color's photos in the card image.

   (4) is the important fix: the image set is derived with the SAME multi-strategy
   filename tokenizer the PDP gallery uses — we pick the first strategy that gives
   every color a DISTINCT token, so a color maps to exactly its own photos. When the
   filenames can't be separated confidently we DON'T guess: we show just that
   variant's featured image instead of wrongly cycling through every image (which
   looked like "cycling from the beginning").
   No app, no extra upfront requests, links/markup untouched (SEO safe).

   Oct 2026 (speed audit, "card diet"): snippets/product-grid-item.liquid no longer
   pre-renders two <img> per colour (nor the theme's all-colours hover slideshow) on
   cards that have swatches. The theme's native click->swap therefore finds no
   per-variant figure and does nothing; (5) below does the swap instead: on a swatch
   click/tap the card's single featured <img> gets the swatch's data-swatch-image
   (variant featured image) as src + a capped srcset, and that becomes the image the
   hover cycle restores to. */
(function () {
  'use strict';
  // Loaded both on the collection template AND globally via the footer section group (so
  // every product grid — home, search, all collections — gets the photo swatches). Guard
  // against running twice when a page includes it more than once.
  if (window.__mojitoCSInit) return;
  window.__mojitoCSInit = true;

  var IMG_W = 180;     // thumbnail render width
  var MAIN_W = 600;    // card main-image width for the cycle
  var CYCLE_MS = 1400; // time per image while cycling a color
  // Only take over hover on hover-capable devices (desktop), matching the theme, which
  // runs its hover slideshow on non-touch only. Touch devices keep the static image.
  var CAN_HOVER = !window.matchMedia || window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  // Sep 2026: collection cards show the COLOR itself (from the theme's swatch color
  // list) instead of the variant photo. Set back to true to restore photo thumbnails —
  // nothing else in this file changes, and the count / active ring / hover cycle are
  // unaffected either way.
  var PAINT_PHOTOS = false;

  function sizedUrl(u, w) {
    if (!u) return '';
    u = u.replace(/([?&])width=\d+/i, '$1').replace(/[?&]$/, '');
    return u + (u.indexOf('?') >= 0 ? '&' : '?') + 'width=' + w;
  }

  /* ---------- filename -> color token (multi-strategy; mirrors the PDP) ----------
     Pick the FIRST strategy that gives every color a DISTINCT token. */
  function cleanName(src) {
    if (!src) return '';
    var b = src.split('?')[0].split('#')[0].split('/').pop();
    b = b.replace(/\.[a-z0-9]+$/i, '');   // drop extension
    b = b.replace(/[_-]\d{1,3}$/, '');    // drop trailing image index (_1 / -2)
    return b;
  }
  function sDashLast(b) { var p = b.split('-'); return (p[p.length - 1] || '').split('_')[0].toUpperCase(); }
  function sUnderscoreLast(b) { if (b.indexOf('_') < 0) return ''; var p = b.split('_'); return (p[p.length - 1] || '').toUpperCase(); }
  function sDashTwo(b) { var p = b.split('-'); if (p.length < 2) return ''; return p.slice(-2).join('-').split('_')[0].toUpperCase(); }
  var STRATS = [sDashLast, sUnderscoreLast, sDashTwo];
  function tokenWith(strat, src) { return strat(cleanName(src)); }

  /* ---------- 1) paint swatch thumbnails (lazy) ---------- */
  function paint(label) {
    if (label.__mojitoCS) return;
    label.__mojitoCS = true;
    if (!PAINT_PHOTOS) return; // color-dot mode: leave the theme's --swatch color alone
    var src = label.getAttribute('data-swatch-image');
    if (!src) return; // no variant photo -> keep the native color dot
    label.style.setProperty('--swatch', 'url("' + sizedUrl(src, IMG_W) + '")');
    label.classList.add('mojito-cs-img');
  }
  var io = ('IntersectionObserver' in window)
    ? new IntersectionObserver(function (entries) {
        entries.forEach(function (e) { if (e.isIntersecting) { paint(e.target); io.unobserve(e.target); } });
      }, { rootMargin: '300px 0px' })
    : null;

  /* ---------- 2) "N colors" count + 3) default active marker (per group) ---------- */
  function selectButton(group, button) {
    if (!group || !button) return;
    var prev = group.querySelectorAll('.swatch__button.mojito-cs-selected');
    for (var i = 0; i < prev.length; i++) prev[i].classList.remove('mojito-cs-selected');
    button.classList.add('mojito-cs-selected');
  }
  // Localized "color"/"colors" noun (set by the section Liquid via window.MojitoCS;
  // honors the active EN/ES locale). Falls back to English if unavailable.
  function colorWord(n) {
    var c = window.MojitoCS || {};
    function bad(s) { return !s || /translation missing/i.test(s); }
    if (n === 1) return bad(c.colorWordOne) ? 'color' : c.colorWordOne;
    return bad(c.colorWordOther) ? 'colors' : c.colorWordOther;
  }

  function initGroup(swatchEl) {
    if (swatchEl.getAttribute('data-mojito-init')) return;
    swatchEl.setAttribute('data-mojito-init', '1');

    // "N color(s)" count above the row — rendered for a SINGLE color too, so
    // single-variant cards keep the same height/alignment as multi-color ones
    // (without it, a 1-color card's swatch row sits higher than its neighbours).
    var n = swatchEl.querySelectorAll('[data-swatch-button]').length;
    var fieldset = swatchEl.closest('.product-grid-item__fieldset--swatches') || swatchEl;
    // Sep 2026: whether to hide the row is decided in Liquid (product-grid-item.liquid
    // stamps data-mojito-onecolor when the product never had more than one color), so
    // a product left with a single IN-STOCK color still shows that color. We only add
    // the attribute here as a backstop when nothing rendered at all.
    if (fieldset.hasAttribute('data-mojito-onecolor') || n < 1) {
      fieldset.setAttribute('data-mojito-onecolor', '');
    } else {
      var hasCount = fieldset.previousElementSibling && fieldset.previousElementSibling.className === 'mojito-cs-count';
      if (!hasCount && fieldset.parentNode) {
        var lbl = document.createElement('div');
        lbl.className = 'mojito-cs-count';
        lbl.textContent = n + ' ' + colorWord(n);
        fieldset.parentNode.insertBefore(lbl, fieldset);
      }
    }

    // mark the first color active by default (matches the card's default image)
    var first = swatchEl.querySelector('.swatch__button');
    if (first) selectButton(swatchEl, first);

    // Take over hover for this color card ONLY if the theme rendered its all-colors
    // slideshow here. Keep the base image visible on hover (strip the theme's
    // hover-animation class so it doesn't fade/slide away) and cycle the SELECTED
    // color's images instead. Cards without the native slideshow are left as-is.
    var card = swatchEl.closest('.product-grid-item');
    // Oct 2026: the holder is no longer rendered on colour cards (card diet), so every
    // colour card on a hover-capable device gets the colour-scoped hover cycle.
    if (CAN_HOVER && card && !card.__mojitoHoverInit) {
      card.__mojitoHoverInit = true;
      var feat = card.querySelector('[data-product-media-featured]');
      if (feat && feat.classList) {
        var strip = [];
        feat.classList.forEach(function (c) { if (/^product__media__image--hover-/.test(c)) strip.push(c); });
        strip.forEach(function (c) { feat.classList.remove(c); });
      }
      card.addEventListener('mouseenter', function () { hoverEnter(card); });
      card.addEventListener('mouseleave', function () { hoverLeave(card); });
    }
  }

  function scan() {
    var labels = document.querySelectorAll('.product-grid-item .swatch__label[data-swatch-image]:not(.mojito-cs-img)');
    for (var i = 0; i < labels.length; i++) {
      var l = labels[i];
      if (l.__mojitoCS) continue;
      if (io) io.observe(l); else paint(l);
    }
    var groups = document.querySelectorAll('product-grid-item-swatch:not([data-mojito-init])');
    for (var g = 0; g < groups.length; g++) { try { initGroup(groups[g]); } catch (e) {} }
  }

  /* ---------- 4) cycle ONLY the selected color's photos ---------- */
  var productCache = {};
  function fetchProduct(handle) {
    if (!handle) return Promise.reject();
    if (productCache[handle]) return Promise.resolve(productCache[handle]);
    return fetch('/products/' + handle + '.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : Promise.reject(); })
      .then(function (p) { productCache[handle] = p; return p; });
  }

  function colorIndexOf(product) {
    var opts = product.options || [];
    for (var i = 0; i < opts.length; i++) {
      var nm = typeof opts[i] === 'string' ? opts[i] : (opts[i] && opts[i].name) || '';
      if (/colou?r/i.test(nm)) return i;
    }
    return opts.length === 1 ? 0 : -1; // single-option product: treat it as the color
  }
  function variantColor(variant, idx) {
    if (idx < 0 || !variant) return null;
    if (variant.options && variant.options.length > idx) return variant.options[idx];
    return variant['option' + (idx + 1)];
  }
  /* Per-product plan: { colorIdx, strat|null, tokenByColor }.
     strat === null => filenames can't separate the colors -> we never cycle through
     everything; we just show that variant's featured image. */
  function planFor(product) {
    if (product.__mojitoPlan) return product.__mojitoPlan;
    var idx = colorIndexOf(product);
    var variants = product.variants || [];
    var repByColor = {}, order = [], c;
    for (var v = 0; v < variants.length; v++) {
      var val = variantColor(variants[v], idx);
      if (val == null) continue;
      var src = variants[v].featured_image && variants[v].featured_image.src;
      if (!(val in repByColor)) { repByColor[val] = src || ''; order.push(val); }
      else if (!repByColor[val] && src) { repByColor[val] = src; }
    }
    var plan = { colorIdx: idx, strat: null, tokenByColor: {} };
    if (order.length >= 2) {
      for (var s = 0; s < STRATS.length; s++) {
        var strat = STRATS[s], map = {}, seen = {}, ok = true, distinct = true;
        for (c = 0; c < order.length; c++) {
          var t = repByColor[order[c]] ? tokenWith(strat, repByColor[order[c]]) : '';
          if (!t) { ok = false; break; }
          if (seen[t]) distinct = false;
          seen[t] = 1; map[order[c]] = t;
        }
        if (ok && distinct) { plan.strat = strat; plan.tokenByColor = map; break; }
      }
    }
    product.__mojitoPlan = plan;
    return plan;
  }

  function variantById(product, id) {
    var variants = product.variants || [];
    for (var i = 0; i < variants.length; i++) { if (String(variants[i].id) === String(id)) return variants[i]; }
    return null;
  }
  function allImageSrcs(product) {
    var media = product.media || [], out = [];
    for (var i = 0; i < media.length; i++) {
      var m = media[i];
      if (m.media_type && m.media_type !== 'image') continue;
      var src = m.src || m;
      if (src) out.push(src);
    }
    return out;
  }
  function colorImages(product, variantId) {
    var v = variantById(product, variantId);
    if (!v) return [];
    var feat = v.featured_image && v.featured_image.src ? v.featured_image.src : '';
    var plan = planFor(product);
    if (!plan.strat) return feat ? [feat] : []; // can't separate -> no broad cycle

    var want = plan.tokenByColor[variantColor(v, plan.colorIdx)];
    if (!want) return feat ? [feat] : [];

    var srcs = [], all = allImageSrcs(product);
    for (var i = 0; i < all.length; i++) { if (tokenWith(plan.strat, all[i]) === want) srcs.push(all[i]); }
    if (!srcs.length) return feat ? [feat] : [];

    // lead the cycle with the variant's own featured image so it starts on-color
    if (feat) {
      var fi = srcs.indexOf(feat);
      if (fi > 0) { srcs.splice(fi, 1); srcs.unshift(feat); }
      else if (fi < 0) { srcs.unshift(feat); }
    }
    return srcs;
  }

  var cycleTimer = null;
  var activeCycleCard = null;
  var touchedFigs = []; // figures whose <img> we swapped this cycle (restored on stop)
  function figureOf(card) {
    return card.querySelector('.product__media--featured-visible') || card.querySelector('[data-main-image]');
  }
  // The card image is a real <img> inside a <figure> (object-fit: cover) — NOT a CSS
  // background — so we cycle by swapping the <img> src (and clearing srcset so src wins).
  // The original src/srcset/sizes are remembered so we can restore the responsive image.
  function paintFig(fig, url) {
    if (!fig) return;
    var img = fig.querySelector('img');
    var u = sizedUrl(url, MAIN_W);
    if (!img) { try { fig.style.backgroundImage = 'url("' + u + '")'; } catch (e) {} return; }
    if (img.__mojitoOrig === undefined) {
      img.__mojitoOrig = {
        src: img.getAttribute('src') || '',
        srcset: img.getAttribute('srcset') || '',
        sizes: img.getAttribute('sizes') || ''
      };
    }
    if (touchedFigs.indexOf(fig) < 0) touchedFigs.push(fig);
    img.removeAttribute('srcset');
    img.removeAttribute('sizes');
    img.classList.remove('is-loading');
    img.src = u;
  }
  function restoreFig(fig) {
    var img = fig && fig.querySelector('img');
    if (!img || img.__mojitoOrig === undefined) return;
    var o = img.__mojitoOrig;
    if (o.srcset) img.setAttribute('srcset', o.srcset); else img.removeAttribute('srcset');
    if (o.sizes) img.setAttribute('sizes', o.sizes); else img.removeAttribute('sizes');
    if (o.src) img.src = o.src;
  }
  /* ---------- 5) swatch click -> swap the card's single featured image (Oct 2026) ----------
     data-swatch-image is the variant's featured image URL without a width param (set in
     product-grid-item.liquid). Build the same capped srcset the card markup uses so the
     browser picks the right size for the column width, keep the card's own `sizes`, and
     record it as the new "original" so stopCycle() restores to THIS colour, not the
     page-load one. Returns false when the card has no <img> to swap (placeholder cards). */
  var SWAP_WIDTHS = [180, 360, 540, 720, 900];
  function swapCardImage(card, url) {
    if (!card || !url) return false;
    var fig = figureOf(card);
    var img = fig && fig.querySelector('img');
    if (!img) return false;
    var base = url.replace(/([?&])width=\d+/i, '$1').replace(/[?&]$/, '');
    var set = [];
    for (var i = 0; i < SWAP_WIDTHS.length; i++) set.push(sizedUrl(base, SWAP_WIDTHS[i]) + ' ' + SWAP_WIDTHS[i] + 'w');
    var sizes = (img.__mojitoOrig && img.__mojitoOrig.sizes) || img.getAttribute('sizes') || '';
    img.__mojitoOrig = { src: sizedUrl(base, MAIN_W), srcset: set.join(', '), sizes: sizes };
    restoreFig(fig);
    return true;
  }
  // Progress bar — our OWN element (full control; the theme's native bar is driven by its
  // hover-slideshow JS, which we disabled, and is gated/hidden). Styled in
  // mojito-collection-swatches.css to match the native look; the JS animates its fill
  // width 0->100% over CYCLE_MS, restarting on each image change.
  function ensureBar(card) {
    if (card.__mojitoBarFill) return;
    var feat = card.querySelector('[data-product-media-featured]') || figureOf(card);
    var host = (feat && (feat.closest('.product-grid-item__image') || feat.parentNode)) || card;
    try { if (getComputedStyle(host).position === 'static') host.style.position = 'relative'; } catch (e) {}
    var bar = document.createElement('div');
    bar.className = 'mojito-cs-progress';
    var fill = document.createElement('div');
    fill.className = 'mojito-cs-progress__fill';
    bar.appendChild(fill);
    host.appendChild(bar);
    card.__mojitoBar = bar;
    card.__mojitoBarFill = fill;
  }
  function tickProgress(card) {
    var bar = card.__mojitoBar, fill = card.__mojitoBarFill;
    if (!bar || !fill) return;
    bar.classList.add('is-on');
    fill.style.transition = 'none';
    fill.style.width = '0%';
    void fill.offsetWidth;                              // reflow so it restarts from empty
    fill.style.transition = 'width ' + CYCLE_MS + 'ms linear';
    fill.style.width = '100%';
  }
  function clearProgress(card) {
    var bar = card && card.__mojitoBar, fill = card && card.__mojitoBarFill;
    if (bar) bar.classList.remove('is-on');
    if (fill) { fill.style.transition = 'none'; fill.style.width = '0%'; }
  }
  function stopCycle() {
    if (cycleTimer) { clearInterval(cycleTimer); cycleTimer = null; }
    if (activeCycleCard) clearProgress(activeCycleCard); // empty the progress bar
    for (var k = 0; k < touchedFigs.length; k++) restoreFig(touchedFigs[k]); // put images back
    touchedFigs = [];
    activeCycleCard = null;
  }
  function startCycle(card, srcs) {
    stopCycle();
    if (!srcs || srcs.length < 2) return; // single image -> nothing to cycle
    activeCycleCard = card;
    ensureBar(card);
    var i = 0;
    // Re-query the visible figure EACH tick: after a swatch click the theme swaps which
    // <figure> is `--featured-visible` (async), so a captured reference would keep painting
    // a now-hidden figure (the bug where cycling "stopped" after a click).
    paintFig(figureOf(card), srcs[0]); // start on the selected color's own featured image
    tickProgress(card);                // fill the bar over CYCLE_MS until the next image
    cycleTimer = setInterval(function () {
      i = (i + 1) % srcs.length;
      paintFig(figureOf(card), srcs[i]);
      tickProgress(card);
    }, CYCLE_MS);
  }

  /* ---- color-scoped HOVER cycle (replaces the theme's all-colors slideshow) ----
     On hover we cycle the SELECTED color's full image set on the base image. The
     theme's own all-colors slideshow overlay is hidden via CSS, and the base image's
     hover-animation class is stripped (initGroup) so it stays put while we cycle it. */
  function selectedVariantOf(card) {
    var btn = card.querySelector('.swatch__button.mojito-cs-selected') || card.querySelector('.swatch__button');
    var el = btn && btn.querySelector('[data-swatch-variant]');
    return el ? el.getAttribute('data-swatch-variant') : null;
  }
  function cycleSelected(card) {
    if (!card.__mojitoHovering) return;
    var swatchEl = card.querySelector('product-grid-item-swatch');
    if (!swatchEl) return;
    var handle = swatchEl.getAttribute('data-swatch-handle');
    var variantId = selectedVariantOf(card);
    if (!handle || !variantId) return;
    fetchProduct(handle).then(function (p) {
      if (!card.__mojitoHovering) return; // pointer left before the fetch resolved
      var srcs = colorImages(p, variantId);
      card.__mojitoSrcs = srcs;
      startCycle(card, srcs);
    }).catch(function () {});
  }
  function hoverEnter(card) {
    card.__mojitoHovering = true;
    if (card.__mojitoHoverTO) clearTimeout(card.__mojitoHoverTO);
    // hover-intent delay: don't fetch for cards you only sweep across
    card.__mojitoHoverTO = setTimeout(function () { cycleSelected(card); }, 120);
  }
  function hoverLeave(card) {
    card.__mojitoHovering = false;
    if (card.__mojitoHoverTO) { clearTimeout(card.__mojitoHoverTO); card.__mojitoHoverTO = null; }
    if (activeCycleCard === card) stopCycle(); // stopCycle restores the cycled <img>
  }

  function onClick(e) {
    var link = e.target && e.target.closest ? e.target.closest('.swatch__link') : null;
    if (!link) return;
    var card = link.closest('.product-grid-item');
    var swatchEl = link.closest('product-grid-item-swatch');
    if (!card || !swatchEl) return;

    // mark the active color immediately (CSS draws the light border on it only)
    var button = link.closest('.swatch__button');
    if (button) selectButton(swatchEl, button);

    // Oct 2026: swap the single card image to this colour ourselves (see header).
    stopCycle();
    var label = button && button.querySelector('.swatch__label[data-swatch-image]');
    swapCardImage(card, label && label.getAttribute('data-swatch-image'));

    // The theme's native swap moves `--featured-visible` to the new color's figure
    // ASYNCHRONOUSLY (after an animation). Halt the old-color cycle now, then restart for
    // the newly-selected color once the swap has settled (startCycle also re-queries the
    // visible figure each tick, so it self-corrects even if the swap runs long).
    stopCycle();
    card.__mojitoHovering = true;
    if (card.__mojitoHoverTO) clearTimeout(card.__mojitoHoverTO);
    card.__mojitoHoverTO = setTimeout(function () { cycleSelected(card); }, 450);
  }

  /* ---------- wiring (handles infinite-scroll / filtered re-renders) ---------- */
  var t = null;
  function scheduleScan() { if (t) return; t = setTimeout(function () { t = null; scan(); }, 200); }
  function start() {
    scan();
    document.addEventListener('click', onClick); // passive: never preventDefault -> native swap intact
    if (window.MutationObserver) {
      new MutationObserver(function (muts) {
        for (var i = 0; i < muts.length; i++) {
          if (muts[i].addedNodes && muts[i].addedNodes.length) { scheduleScan(); return; }
        }
      }).observe(document.body, { childList: true, subtree: true });
    }
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
